import { ClosableDialog } from 'components/controls/dialogs'
import { Button } from 'components/facets'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import ImagePopover from 'components/facets/ImagePopover'
import { useCardData } from 'components/providers/CardDataProvider'
import { cardtraderAlignmentCancel, cardtraderAlignmentConfirm } from 'lib/api'
import { useErrorHandler } from 'lib/hooks'
import {
  CardtraderAlignmentStaleItem,
  CardtraderSkippedListing,
} from 'lib/hooks/useCardtraderAlignmentStatus'
import { getCurrencySymbol } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useMemo, useState } from 'react'
import { CSVLink } from 'react-csv'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table } from 'react-virtualized'
import {
  Article,
  Card,
  CardLanguage,
  CardmarketCondition,
  InventoryArticle,
  mapBackendCountryEditionToInternal,
  mapBackendFinishAttributeToInternal,
} from 'shared'
import { getProductImageUrl } from 'utils/imageUtils'

interface CardtraderReviewDialogProps {
  open: boolean
  fetchedAt: string | null
  staleItems: CardtraderAlignmentStaleItem[]
  skippedListings: CardtraderSkippedListing[]
  onClose: () => void
  onResolved: () => void
}

interface StaleRow {
  inventoryArticle: InventoryArticle
  card: Card
  ctProductId: number
  quantity: number
  price: number | null
  currency: string | null
  comment: string
}

const TABLE_HEIGHT = 500
const TABLE_MAX_VIEWPORT_SHARE = '50vh'
const TABLE_MIN_HEIGHT = 300

const formatFetchedAt = (iso: string | null): string => {
  if (!iso) return ''
  const date = new Date(iso)
  return date.toLocaleString()
}

const buildStaleRows = (
  items: CardtraderAlignmentStaleItem[],
  cardDataMap: Map<number, Card>
): StaleRow[] => {
  const rows: StaleRow[] = []
  for (const item of items) {
    const card = cardDataMap.get(item.ptProduct.mkmProductId)
    if (!card) continue
    const article = new Article({
      idProduct: item.ptProduct.mkmProductId,
      quantity: item.quantity,
      condition: item.ptProduct.condition as CardmarketCondition,
      idLanguage: item.ptProduct.idLanguage as CardLanguage,
      price: item.price ?? 0,
      comment: item.comment ?? '',
      location: item.comment ?? '',
      finishType: mapBackendFinishAttributeToInternal(item.ptProduct.finishType),
      isSigned: item.ptProduct.isSigned,
      isFirstEd: item.ptProduct.isFirstEd,
      countryEdition: mapBackendCountryEditionToInternal(item.ptProduct.countryEdition ?? 0),
      gradingCompany: item.ptProduct.gradingCompany ?? undefined,
      grade: item.ptProduct.grade ?? undefined,
    })
    const inventoryArticle = new InventoryArticle({
      attributes: article.attributes,
      card: card.toPlainObject(),
      ptArticleReservedQuantity: 0,
      location: article.comment,
      ptProductId: item.ptProduct._id,
    })
    rows.push({
      inventoryArticle,
      card,
      ctProductId: item.ctProductId,
      quantity: item.quantity,
      price: item.price,
      currency: item.currency,
      comment: item.comment ?? '',
    })
  }
  return rows
}

export const CardtraderReviewDialog = ({
  open,
  fetchedAt,
  staleItems,
  skippedListings,
  onClose,
  onResolved,
}: CardtraderReviewDialogProps): JSX.Element => {
  const [submitting, setSubmitting] = useState(false)
  const { handle } = useErrorHandler()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const cardData = useCardData()

  const cardDataMap = useMemo(() => {
    const map = new Map<number, Card>()
    for (const card of cardData) map.set(card._id, card)
    return map
  }, [cardData])

  const rows = useMemo(() => buildStaleRows(staleItems, cardDataMap), [staleItems, cardDataMap])

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      await cardtraderAlignmentConfirm()
      enqueueSnackbar(t('connectCardtrader.salesChannelSync.snackbar.confirmed'), {
        variant: 'success',
      })
      onResolved()
      onClose()
    } catch (error) {
      handle(error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = async () => {
    setSubmitting(true)
    try {
      await cardtraderAlignmentCancel()
      enqueueSnackbar(t('connectCardtrader.salesChannelSync.snackbar.discarded'), {
        variant: 'info',
      })
      onResolved()
      onClose()
    } catch (error) {
      handle(error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ClosableDialog maxWidth="xl" open={open} onClose={onClose} hideButton fullWidth>
      <h3>{t('connectCardtrader.salesChannelSync.review.title')}</h3>
      <span>
        {t('connectCardtrader.salesChannelSync.review.fetchedAt', {
          date: formatFetchedAt(fetchedAt),
        })}
      </span>
      <p className="fs-6">{t('connectCardtrader.salesChannelSync.review.staleExplanation')}</p>
      <p className="fs-6">{t('connectCardtrader.salesChannelSync.review.actionsExplanation')}</p>

      {skippedListings.length > 0 && (
        <p className="fs-6">
          {t('connectCardtrader.salesChannelSync.review.skippedListings', {
            count: skippedListings.length,
          })}{' '}
          <CSVLink
            data={skippedListings}
            filename="cardtrader-not-imported.csv"
            enclosingCharacter={'"'}
          >
            {t('connectCardtrader.salesChannelSync.review.downloadSkippedListings')}
          </CSVLink>
        </p>
      )}
      <div
        className="mb-4"
        style={{
          height: TABLE_HEIGHT,
          maxHeight: TABLE_MAX_VIEWPORT_SHARE,
          minHeight: TABLE_MIN_HEIGHT,
          width: '100%',
        }}
      >
        <AutoSizer>
          {({ width, height }) => (
            <Table
              width={width}
              height={height}
              headerHeight={40}
              rowHeight={112}
              rowCount={rows.length}
              rowGetter={({ index }) => rows[index]}
            >
              <Column
                width={340}
                flexGrow={2}
                flexShrink={1}
                label={t('connectCardtrader.salesChannelSync.review.columnName')}
                dataKey="card"
                disableSort
                cellRenderer={({ rowData }: { rowData: StaleRow }) => (
                  <div className="d-flex flex-row justify-content-start align-items-start">
                    <div className="me-2">
                      <ImagePopover
                        variant="imagePopover"
                        imageComponent={
                          <img
                            width="62px"
                            src={getProductImageUrl(rowData.card._id)}
                            alt={rowData.card.name}
                          />
                        }
                        imageId={String(rowData.card._id)}
                      />
                    </div>
                    <div className="d-flex flex-column">
                      <span className="fw-semibold">{rowData.card.name}</span>
                      <span>{rowData.card.expansion}</span>
                      <span>
                        #{rowData.card.cn} - {rowData.card.set}
                      </span>
                      <div className="mt-2">
                        <ArticleAttributes
                          article={rowData.inventoryArticle}
                          identifierForDataTestId={`stale-${rowData.ctProductId}-`}
                          iconHeight={17}
                        />
                      </div>
                    </div>
                  </div>
                )}
              />
              <Column
                width={220}
                flexGrow={1}
                flexShrink={1}
                label={t('connectCardtrader.salesChannelSync.review.columnAttributes')}
                dataKey="attributes"
                disableSort
                cellRenderer={({ rowData }: { rowData: StaleRow }) => (
                  <div className="d-flex flex-row align-items-center">
                    <span className="me-3">{rowData.quantity}x </span>

                    <ArticleAttributes
                      article={rowData.inventoryArticle}
                      identifierForDataTestId={`stale-${rowData.ctProductId}-`}
                      iconHeight={17}
                    />
                  </div>
                )}
              />

              <Column
                width={200}
                flexGrow={1}
                flexShrink={1}
                label={t('connectCardtrader.salesChannelSync.review.columnComment')}
                dataKey="comment"
                disableSort
                cellRenderer={({ rowData }: { rowData: StaleRow }) => (
                  <span style={{ overflowWrap: 'anywhere' }}>{rowData.comment || '—'}</span>
                )}
              />

              <Column
                width={120}
                flexShrink={0}
                label={t('connectCardtrader.salesChannelSync.review.columnPrice')}
                dataKey="price"
                disableSort
                cellRenderer={({ rowData }: { rowData: StaleRow }) => (
                  <span>
                    {rowData.price != null
                      ? `${rowData.price} ${
                          rowData.currency ? getCurrencySymbol(rowData.currency) : ''
                        }`
                      : '—'}
                  </span>
                )}
              />
            </Table>
          )}
        </AutoSizer>
      </div>
      <div className="d-flex justify-content-end" style={{ gap: 12, marginTop: 16 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleCancel}
          disabled={submitting}
          data-testid="cardtrader-review-cancel"
        >
          {t('connectCardtrader.salesChannelSync.review.cancelButton')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={submitting}
          data-testid="cardtrader-review-confirm"
        >
          {t('connectCardtrader.salesChannelSync.review.confirmButton')}
        </Button>
      </div>
    </ClosableDialog>
  )
}
